import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Bell, CheckCheck, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { fmtDateTime } from '../lib/utils';
import { toast } from 'sonner';

const PAGE = 25;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [allTotal, setAllTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (reset = true, skip = 0) => {
    setLoading(true);
    try {
      const r = await api.get('/notifications/all', { params: { skip, limit: PAGE, unread_only: filter === 'unread' } });
      setItems((prev) => (reset ? r.data.items : [...prev, ...r.data.items]));
      setTotal(r.data.total);
      setAllTotal(r.data.all_total ?? r.data.total);
      setUnread(r.data.unread);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(true, 0); }, [load]);

  const markRead = async (n) => {
    if (n.read) return;
    await api.post(`/notifications/${n.id}/read`);
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
  };

  const markAll = async () => {
    await api.post('/notifications/read-all');
    toast.success('All notifications marked as read');
    load(true, 0);
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="notifications-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
            <button onClick={() => setFilter('all')} className={`px-4 py-2 ${filter === 'all' ? 'bg-navy text-white' : 'bg-white text-slate-600'}`} data-testid="notif-filter-all">All ({allTotal})</button>
            <button onClick={() => setFilter('unread')} className={`px-4 py-2 ${filter === 'unread' ? 'bg-navy text-white' : 'bg-white text-slate-600'}`} data-testid="notif-filter-unread">Unread ({unread})</button>
          </div>
        </div>
        {unread > 0 && (
          <Button variant="outline" size="sm" onClick={markAll} data-testid="notifpage-mark-all">
            <CheckCheck className="w-4 h-4 mr-1.5" /> Mark all as read
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100">
        {items.length === 0 && !loading && (
          <div className="py-16 text-center text-slate-500">
            <Bell className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            No notifications{filter === 'unread' ? ' unread' : ''}
          </div>
        )}
        {items.map((n) => (
          <div
            key={n.id}
            className={`flex gap-3 px-5 py-4 hover:bg-slate-50 transition ${!n.read ? 'bg-magenta/[0.03]' : ''}`}
            data-testid={`notifpage-item-${n.id}`}
          >
            <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${n.read ? 'bg-slate-200' : 'bg-magenta'}`} data-testid={`notif-dot-${n.id}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-sm ${n.read ? 'font-medium text-slate-700' : 'font-bold text-navy'}`}>{n.title}</span>
                <span className="text-[11px] text-slate-400 ml-auto shrink-0">{fmtDateTime(n.created_at)}</span>
              </div>
              <p className="text-sm text-slate-600 mt-0.5">{n.message}</p>
              <div className="flex items-center gap-3 mt-1.5">
                {n.link && (
                  <button
                    className="text-xs font-semibold text-magenta hover:underline inline-flex items-center gap-1"
                    onClick={() => { markRead(n); navigate(n.link); }}
                    data-testid={`notif-open-${n.id}`}
                  >
                    <ExternalLink className="w-3 h-3" /> Open related record
                  </button>
                )}
                {!n.read && (
                  <button className="text-xs text-slate-500 hover:text-navy hover:underline" onClick={() => markRead(n)} data-testid={`notif-read-${n.id}`}>
                    Mark as read
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {items.length < total && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => load(false, items.length)} disabled={loading} data-testid="notif-load-more">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Load more ({items.length} of {total})
          </Button>
        </div>
      )}
    </div>
  );
}
